import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import appLogo from '../assets/branding/logo.png';

function ThemeIcon({ theme }) {
  return theme === 'dark' ? (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1.5M12 19.5V21M4.22 4.22l1.06 1.06M18.72 18.72l1.06 1.06M3 12h1.5M19.5 12H21M4.22 19.78l1.06-1.06M18.72 5.28l1.06-1.06" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  ) : (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.2 15.2A8.5 8.5 0 0 1 8.8 3.8 8.5 8.5 0 1 0 20.2 15.2Z" />
    </svg>
  );
}

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => setIsMenuOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMenuOpen]);

  const isActive = (path) => location.pathname === path;
  const displayName = user?.name || user?.email || 'Editor';

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="site-header">
      <nav className="nav-shell" aria-label="Primary navigation">
        <Link to="/" className="brand-lockup" aria-label="KinetoCut home">
          <img src={appLogo} alt="" className="brand-logo" />
          <span className="brand-copy">
            <strong>Kineto<span>Cut</span></strong>
            <small>Video editor</small>
          </span>
        </Link>

        <div className="desktop-nav nav-links">
          <Link className={isActive('/') && !location.hash ? 'active' : ''} to="/">Home</Link>
          <Link to="/#features">Features</Link>
          <Link className={isActive('/support') ? 'active' : ''} to="/support">Support</Link>
        </div>

        <div className="desktop-nav nav-actions">
          <button
            className="icon-button"
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            <ThemeIcon theme={theme} />
          </button>
          {isAuthenticated ? (
            <>
              <Link to="/profile" className="user-chip">
                <span className="user-avatar">{displayName[0].toUpperCase()}</span>
                <span>{displayName}</span>
              </Link>
              <button className="button button-quiet" type="button" onClick={handleLogout} disabled={isLoggingOut}>
                {isLoggingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="button button-quiet">Sign in</Link>
              <Link to="/register" className="button button-primary button-compact">Get started</Link>
            </>
          )}
        </div>

        <div className="mobile-nav-actions">
          <button
            className="icon-button"
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            <ThemeIcon theme={theme} />
          </button>
          <button
            className="icon-button menu-toggle"
            type="button"
            onClick={() => setIsMenuOpen((value) => !value)}
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              {isMenuOpen ? (
                <path strokeLinecap="round" d="m6 6 12 12M18 6 6 18" />
              ) : (
                <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      <div
        id="mobile-navigation"
        className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}
        aria-hidden={!isMenuOpen}
        hidden={!isMenuOpen}
      >
        <div className="mobile-menu-inner">
          {isAuthenticated && (
            <div className="mobile-user">
              <span className="user-avatar">{displayName[0].toUpperCase()}</span>
              <span><strong>{displayName}</strong><small>Signed in</small></span>
            </div>
          )}
          <Link className={isActive('/') ? 'active' : ''} to="/">Home</Link>
          <Link to="/#features">Features</Link>
          <Link className={isActive('/support') ? 'active' : ''} to="/support">Support</Link>
          <div className="mobile-menu-actions">
            {isAuthenticated ? (
              <>
                <Link to="/profile" className="button button-secondary">Profile</Link>
                <button className="button button-quiet" type="button" onClick={handleLogout} disabled={isLoggingOut}>
                  {isLoggingOut ? 'Signing out…' : 'Sign out'}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="button button-secondary">Sign in</Link>
                <Link to="/register" className="button button-primary">Create account</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
